import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { Check, ClipboardList, Pencil, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import api from '../services/api';
import PageIntroDialog from '../components/PageIntroDialog';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: '16px',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

const normalizeForm = (value) => ({
  id: value?.id != null ? Number(value.id) : null,
  codice: String(value?.codice || '').trim(),
  nome: String(value?.nome || '').trim(),
  ordine: value?.ordine != null ? Number(value.ordine) : 0
});

export default function FasiGestione() {
  const [introOpen, setIntroOpen] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(normalizeForm({}));

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const fetchRows = async (searchArg = search) => {
    setLoading(true);
    try {
      const res = await api.get('/richieste/fasi-gestione', { params: { search: String(searchArg || '').trim() } });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore caricamento');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows('');
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchRows(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filteredRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredRows.slice(start, end);
  }, [filteredRows, page, rowsPerPage]);

  const openNew = () => {
    setForm(normalizeForm({ ordine: 0 }));
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm(normalizeForm(row));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setForm(normalizeForm({}));
  };

  const save = async () => {
    if (saving) return;
    const isEdit = Number.isInteger(form?.id) && form.id > 0;
    const codice = String(form?.codice || '').trim().toUpperCase();
    const nome = String(form?.nome || '').trim();
    const ordine = Number.isFinite(Number(form?.ordine)) ? Number(form.ordine) : 0;

    if (!codice) {
      toast.error('Inserisci il codice');
      return;
    }
    if (!/^[A-Z0-9_]+$/.test(codice)) {
      toast.error('Codice non valido (usa A-Z, 0-9, underscore)');
      return;
    }
    if (!nome) {
      toast.error('Inserisci il nome');
      return;
    }

    try {
      setSaving(true);
      if (isEdit) {
        await api.put(`/richieste/fasi-gestione/${form.id}`, { codice, nome, ordine });
        toast.success('Fase aggiornata');
      } else {
        await api.post('/richieste/fasi-gestione', { codice, nome, ordine });
        toast.success('Fase creata');
      }
      closeDialog();
      await fetchRows(search);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => {
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const confirmDelete = async () => {
    if (deleting) return;
    const id = Number(deleteRow?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    try {
      setDeleting(true);
      await api.delete(`/richieste/fasi-gestione/${id}`);
      toast.success('Fase eliminata');
      closeDelete();
      await fetchRows(search);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageIntroDialog
        open={introOpen}
        onClose={() => setIntroOpen(false)}
        title="Fasi gestione"
        subtitle="Lookup delle fasi del workflow utilizzato nelle richieste."
        icon={ClipboardList}
        color="#f59e0b"
        bullets={[
          'Serve per classificare e ordinare le fasi (es. Richiesta Documenti, Diffida, ecc.)',
          'Il codice è un identificativo stabile per regole e integrazioni',
          'Le regole modelli si agganciano a una fase specifica'
        ]}
        example="Esempio: crea la fase “DIFFIDA” con codice DIFFIDA e ordine 20, così potrai associarle i modelli dedicati."
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: alpha('#f59e0b', 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ClipboardList size={20} color="#f59e0b" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              Fasi gestione
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lookup fasi (Richiesta Documenti, Diffida, Sollecito Risposta, ecc.)
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button variant="outline" onClick={() => fetchRows(search)} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={openNew}>
            <Plus className="mr-2 h-4 w-4" />
            Nuova fase
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <CardContent>
          <Grid
            container
            spacing={2}
            sx={{
              mb: 3,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 6 }
            }}
          >
            <Grid item sx={{ minWidth: 420, flex: '0 0 auto' }}>
              <TextField
                placeholder="Cerca per codice o nome..."
                variant="outlined"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 220 }}>Codice</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Ordine</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120, textAlign: 'right' }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 6 }}>
                      <Typography align="center" color="text.secondary">
                        Caricamento...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : pagedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 6 }}>
                      <Typography align="center" color="text.secondary">
                        Nessun record trovato
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Paper
                          variant="outlined"
                          sx={(theme) => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '999px',
                            fontWeight: 800,
                            fontSize: 12,
                            bgcolor: theme.palette.mode === 'dark' ? alpha('#0b1220', 0.6) : '#f8fafc'
                          })}
                        >
                          {row.codice}
                        </Paper>
                      </TableCell>
                      <TableCell>{row.nome}</TableCell>
                      <TableCell>{Number(row.ordine || 0)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Modifica" arrow>
                          <span>
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <Pencil size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Elimina" arrow>
                          <span>
                            <IconButton size="small" onClick={() => openDelete(row)} sx={{ color: '#ef4444' }}>
                              <Trash2 size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10) || 25;
              setRowsPerPage(next);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </StyledCard>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{Number.isInteger(form?.id) && form.id > 0 ? 'Modifica fase' : 'Nuova fase'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Codice"
                size="small"
                fullWidth
                value={form.codice}
                onChange={(e) => setForm((prev) => ({ ...prev, codice: e.target.value.toUpperCase() }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Nome" size="small" fullWidth value={form.nome} onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Ordine"
                size="small"
                fullWidth
                type="number"
                value={Number.isFinite(Number(form.ordine)) ? Number(form.ordine) : 0}
                onChange={(e) => setForm((prev) => ({ ...prev, ordine: Number(e.target.value || 0) }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDialog} disabled={saving}>
            <X className="mr-2 h-4 w-4" />
            Annulla
          </Button>
          <Button onClick={save} disabled={saving}>
            <Check className="mr-2 h-4 w-4" />
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Elimina fase</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Confermi eliminazione di "{String(deleteRow?.nome || '')}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDelete} disabled={deleting}>
            <X className="mr-2 h-4 w-4" />
            Annulla
          </Button>
          <Button onClick={confirmDelete} disabled={deleting}>
            <Trash2 className="mr-2 h-4 w-4" />
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
